// 1. React
import { memo, useCallback, useEffect, useMemo } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { OfferHeaderProps } from "./types";

// 4. Form hooks
import {
    useHandleDateChange,
    useHandleDropdownChange,
    useHandleNumberFieldChange,
} from "services/forms/hooks";

// 6. UI Components
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import { DatePicker } from "ui/components/DatePicker";
import { NumberField } from "ui/components/TextField/NumberField";
import { TextField } from "ui/components/TextField/TextField";
import { RentPeriodFields, RentPeriodValues } from "ui/components/DateRangeRentPicker";

// 5. Šifrarnik stavki
import { getBillingItemTypesAPI } from "services/api/finance/billing/api";
import { calculateBillingLineTotal } from "services/api/finance/billing/calculations";
import { DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

const THIRTY_MINUTES = 30 * 60 * 1000;

const OfferHeader: React.FC<OfferHeaderProps> = (props) => {
    const { formValues, setFormValues, errors } = props;

    const { t } = useTranslate();

    const handleDropdownChange = useHandleDropdownChange(
        setFormValues,
        formValues
    );

    const handleDateChange = useHandleDateChange(setFormValues, formValues);

    const handleNumberFieldChange = useHandleNumberFieldChange(
        setFormValues,
        formValues
    );

    const { data: billingItemTypes } = useDropdownCache(
        DropdownCacheKey.BillingItemTypes,
        getBillingItemTypesAPI,
        THIRTY_MINUTES
    );

    /** Stavka najamnine — ona nosi mjesečnu cijenu (cijena po jedinici) i broj mjeseci (količina). */
    const recurringItem = useMemo(
        () => formValues.items.find(item =>
            !item.deleted
            && !!item.billingItemTypeId
            && (billingItemTypes ?? []).some(type => type.id === item.billingItemTypeId && type.isRecurring)
        ),
        [formValues.items, billingItemTypes]
    );

    // Mjesečna cijena se ne upisuje zasebno nego se čita iz stavke — inače isti podatak stoji
    // na dva mjesta i tiho se raziđe. Ovdje se samo prepisuje u obrazac zbog PDF-a i ugovora.
    useEffect(() => {
        // Dok se šifrarnik ne učita ne zna se koja je stavka najamnina — bez ovoga bi se
        // postojeća cijena na trenutak obrisala pri otvaranju ponude.
        if (!billingItemTypes) return;

        const priceFromItem = recurringItem?.priceWithoutTax ?? null;

        if (priceFromItem === formValues.monthlyPrice) return;

        setFormValues(prev => ({ ...prev, monthlyPrice: priceFromItem }));
    }, [billingItemTypes, recurringItem, formValues.monthlyPrice, setFormValues]);

    // Razdoblje mijenja više polja odjednom (početak pomiče završetak, završetak prerači trajanje),
    // a trajanje je ujedno količina na stavci najamnine — 12 mj × mjesečna cijena.
    const handleRentPeriodChange = useCallback((patch: Partial<RentPeriodValues>) => {
        setFormValues(prev => {
            const next = { ...prev, ...patch };
            const months = patch.rentDurationMonths;

            if (!months || !recurringItem) return next;

            next.items = prev.items.map(item => item.id !== recurringItem.id ? item : {
                ...item,
                quantity: months,
                total: calculateBillingLineTotal({ ...item, quantity: months }),
            });

            return next;
        });
    }, [setFormValues, recurringItem]);

    return (
        <>
            <div className="flex flex-col gap-3 mb-3">
                <div className="flex gap-4">
                    <div className="w-1/3">
                        <TextField
                            label={t("admin.fleet.vehicle.offer.rent.item.add.offernumber")}
                            type="text"
                            name="offerNumber"
                            value={formValues.offerNumber}
                            error={errors.name}
                            required
                            disabled
                        />
                    </div>
                    <div className="w-2/3">
                        <VehicleDropdown
                            name="vehicleId"
                            label={t("admin.fleet.vehicle.offer.rent.add.vehicle")}
                            placeholder={t(
                                "admin.fleet.vehicle.offer.rent.add.vehicle.placeholder"
                            )}
                            value={formValues.vehicleId}
                            onChange={handleDropdownChange}
                            error={errors.vehicleId}
                            required
                            disabled={!!formValues.vehicleId}
                        />
                    </div>

                </div>
                <div className="flex gap-4">
                    <div className="flex-1">
                        <DatePicker
                            name="offerDate"
                            label={t("admin.fleet.vehicle.offer.rent.add.offerdate")}
                            placeholder={t(
                                "admin.fleet.vehicle.offer.rent.add.offerdate.placeholder"
                            )}
                            onChange={handleDateChange}
                            error={errors.offerDate}
                            value={formValues.offerDate}
                            width="full"
                            required
                        />
                    </div>
                    <div className="flex-1">
                        <DatePicker
                            name="dueDate"
                            label={t("admin.fleet.vehicle.offer.rent.add.duedate")}
                            placeholder={t(
                                "admin.fleet.vehicle.offer.rent.add.duedate.placeholder"
                            )}
                            onChange={handleDateChange}
                            error={errors.dueDate}
                            value={formValues.dueDate}
                            width="full"
                            required
                        />
                    </div>
                    <div className="flex flex-1 items-end ">
                        <label className="block text-sm mb-2 mr-3 font-medium leading-6 text-[#344054]">
                            {t("admin.fleet.vehicle.offer.rent.add.durationdays")}
                        </label>
                        <div className="flex-1">
                            <NumberField
                                name="durationDays"
                                onChange={handleNumberFieldChange}
                                value={formValues.durationDays}
                                decimals={0}
                            ></NumberField>
                        </div>

                    </div>
                </div>

                <div className="flex gap-4">

                  <div className="flex-1">
                        <ContactDropdown
                            placeholder={t(
                                "admin.fleet.vehicle.offer.rent.add.contact.client.placeholder"
                            )}
                            label={t("admin.fleet.vehicle.offer.rent.add.contact.client")}
                            name="contactClientId"
                            value={formValues.contactClientId}
                            onChange={handleDropdownChange}
                            error={errors.contactClientId}
                            required
                        />
                    </div>
                    <div className="flex-1">
                        <ContactDropdown
                            placeholder={t(
                                "admin.fleet.vehicle.offer.rent.add.contact.consumer.placeholder"
                            )}
                            label={t("admin.fleet.vehicle.offer.rent.add.contact.consumer")}
                            name="contactConsumerId"
                            value={formValues.contactConsumerId}
                            onChange={handleDropdownChange}
                            error={errors.contactConsumerId}
                        />
                    </div>
                    {/* Prazna trećina — kontakti ostaju iste širine kao na prodajnoj ponudi. */}
                    <div className="flex-1"></div>
                </div>

                {/* Najam je vlastita cjelina — odvojena linijama gore i dolje kao sekcije na ugovoru. */}
                <div className="border-y border-slate-200 py-4 px-4 -mx-4">
                    <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Najam</div>
                    {/* Razdoblje zauzima trećinu, kao i ostala polja u zaglavlju. */}
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <RentPeriodFields
                                rentStartDate={formValues.rentStartDate}
                                rentEndDate={formValues.rentEndDate}
                                rentDurationMonths={formValues.rentDurationMonths}
                                monthlyPrice={formValues.monthlyPrice}
                                onChange={handleRentPeriodChange}
                                errors={errors}
                            />
                        </div>
                        <div className="flex-1"></div>
                        <div className="flex-1"></div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default memo(OfferHeader);
